import * as React from 'react'
import axios from 'axios'
import Question from './Question'
import ConfirmDialog from './ConfirmDialog'

// Prop keys arrive camelized (config.react.camelize_props), so the YAML's
// `visible_when` reads as `visibleWhen` here. A question with that key only
// shows once the question it points at holds the given value. Hidden questions
// are never validated and their answers are dropped server-side, so answering
// "No" to the gala dinner question skips its follow-ups entirely.
const isVisible = (question, answers) => {
  const condition = question.visibleWhen
  if (!condition) return true

  return answers[condition.code] === condition.equals
}

const isBlank = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const SurveyForm = ({ form, submitUrl }) => {
  const [answers, setAnswers] = React.useState({})
  const [errors, setErrors] = React.useState({})
  const [submitting, setSubmitting] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)

  const setAnswer = (code, value) => {
    setAnswers((current) => ({ ...current, [code]: value }))
    setErrors((current) => {
      if (!current[code]) return current

      const next = { ...current }
      delete next[code]
      return next
    })
  }

  const validate = () => {
    const found = {}

    form.sections.forEach((section) => {
      section.questions.forEach((question) => {
        if (!question.required || !isVisible(question, answers)) return
        if (isBlank(answers[question.code])) {
          found[question.code] = 'This question is required.'
        }
      })
    })

    return found
  }

  // Only answers to currently visible questions are sent; the server rebuilds
  // and re-validates the payload against the same definition either way.
  const visibleAnswers = () => {
    const payload = {}

    form.sections.forEach((section) => {
      section.questions.forEach((question) => {
        if (isVisible(question, answers) && !isBlank(answers[question.code])) {
          payload[question.code] = answers[question.code]
        }
      })
    })

    return payload
  }

  const focusFirstError = (found) => {
    const first = Object.keys(found)[0]
    const element = document.getElementById(`question-${first}`)
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  // Submitting is one-shot — a delegate cannot come back and edit — so the
  // answers are validated first and then held behind a confirmation step.
  const handleSubmit = (event) => {
    event.preventDefault()
    if (submitting) return

    const found = validate()
    setErrors(found)

    if (Object.keys(found).length > 0) {
      focusFirstError(found)
      return
    }

    setConfirming(true)
  }

  const performSubmit = async () => {
    setConfirming(false)
    setSubmitting(true)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')
      const response = await axios.post(
        submitUrl,
        { answers: visibleAnswers() },
        {
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': token ? token.content : '',
          },
        }
      )
      window.location.href = response.data.redirect_url
    } catch (error) {
      const data = error.response && error.response.data

      if (data && data.redirect_url) {
        window.location.href = data.redirect_url
        return
      }

      if (data && data.errors) {
        setErrors(data.errors)
        focusFirstError(data.errors)
      } else {
        alert('Sorry, we could not submit your feedback. Please try again.')
      }
      setSubmitting(false)
    }
  }

  return (
    <form className="survey-form" onSubmit={handleSubmit} noValidate>
      <h1 className="survey-title">{form.title}</h1>
      {form.intro.map((paragraph, index) => (
        <p key={index} className="survey-intro">
          {paragraph}
        </p>
      ))}

      {form.sections.map((section) => {
        const questions = section.questions.filter((question) => isVisible(question, answers))
        if (questions.length === 0) return null

        return (
          <section key={section.title} className="survey-section">
            <h2 className="section-title">{section.title}</h2>
            {section.description && <p className="section-description">{section.description}</p>}
            <hr />

            {questions.map((question) => (
              <Question
                key={question.code}
                question={question}
                scale={form.ratingScale}
                value={answers[question.code]}
                error={errors[question.code]}
                onChange={(value) => setAnswer(question.code, value)}
              />
            ))}
          </section>
        )
      })}

      <div className="survey-actions">
        <button type="submit" className="btn submit-survey" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Feedback'}
        </button>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Submit your feedback?"
          message="Your answers cannot be changed once submitted."
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          onConfirm={performSubmit}
          onCancel={() => setConfirming(false)}
        />
      )}
    </form>
  )
}

export default SurveyForm
